import React, { createContext, useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { addNotification, getNotificationCount, resetNotifications } from '../Website/redux/notification';

const WebSocketContext = createContext();

export const useWebSocket = () => useContext(WebSocketContext);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "https://api.electrowiz.in";

// Ignore notifications that the server tagged for a different user
const isForUser = (notification, userId) => {
    const target = notification?.userId || notification?.receiverId || notification?.recipientId;
    if (!target) return true;
    const targetId = typeof target === 'object' ? target._id : target;
    return String(targetId) === String(userId);
};

export const WebSocketProvider = ({ children }) => {
    const dispatch = useDispatch();
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const user = useSelector((state) => state.customer);
    const userId = user?.customer?._id;

    useEffect(() => {
        // New user (or logged out) -> drop whatever belonged to the previous user
        dispatch(resetNotifications());

        if (!userId) return;

        dispatch(getNotificationCount());

        const newSocket = io(SOCKET_URL, {
            // Polling first, then upgrade: if the server refuses websocket the connection stays on polling
            transports: ["polling", "websocket"],
            upgrade: true,
            reconnection: true,
            reconnectionDelay: 2000,
        });
        setSocket(newSocket);

        newSocket.on("connect", () => {
            setIsConnected(true);
            // Re-register on every (re)connect so the server maps this socket to this user only
            newSocket.emit("register", userId);
        });

        newSocket.on("disconnect", () => {
            setIsConnected(false);
        });

        newSocket.on("connect_error", (error) => {
            console.log("Socket.IO connection error:", error?.message);
        });

        newSocket.on("notification", (notification) => {
            if (!isForUser(notification, userId)) return;
            dispatch(addNotification(notification));
            dispatch(getNotificationCount());
            if (notification?.title) toast.info(notification.title);
        });

        return () => {
            newSocket.removeAllListeners();
            newSocket.disconnect();
            setSocket(null);
            setIsConnected(false);
        };
    }, [userId, dispatch]);

    return (
        <WebSocketContext.Provider value={{ socket, isConnected }}>
            {children}
        </WebSocketContext.Provider>
    );
};
